import { useState, useCallback } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  GreenContainer,
  GreenContainerQuestionWrapper,
  ClaimsContainer
} from '../../../components/application/defaultComponents'
import PrebuiltRenewalFooter from '../../../components/application/PrebuiltRenewalFooter'
import { useRenewal } from '../../../context/renewal'
import { LeftBar } from 'src/components/basic/LeftBar'
import { FlexDiv } from 'src/components/application/formComponents/styles'
import StepBullet from 'src/components/basic/StepBullet'
import OptionButton from 'src/components/basic/OptionButton'
import ClaimItemForm from 'src/components/application/formComponents/ClaimItemForm'
import flagWithCheckImg from 'src/assets/applicationFormsImgs/flag_with_check.svg'
import { ClaimData } from 'src/dtos/applicationData'
import Button from 'src/components/basic/Button'
import { isClaimsTriggered, allActiveAttorneys } from 'src/utils/renewals'
import { RenewalData } from 'src/dtos/renewalData'
import { string } from 'yup'

interface MalpracticeData {
  isVisible: boolean;
  value: boolean | undefined;
}

const Claims = () => {
  const firmRenewalClaimErrorCodes = ['EC111', 'EC112', 'EC113', 'EC114', 'EC115', 'EC116', 'EC117', 'EC118', 'EC119',
                                      'EC123', 'EC124', 'EC125', 'EC126', 'EC127', 'EC128', 'EC129', 'EC130', 'EC131',
                                      'EC132', 'EC133', 'EC134', 'EC302', 'EC303', 'EC304', 'EC305',]
  const { renewalData } = useRenewal()
  const [claims, setClaims] = useState<ClaimData[]>(() => {
    return renewalData?.renewalSection?.claims || [{}]
  })
  const [isIncorrect, setIsIncorrect] = useState(false)
  const [hasMalpracticeClaim, setHasMalpracticeClaim] = useState<MalpracticeData>(() => {
    if(renewalData?.renewalSection?.hasMalPracticeClaims) {
      const { hasMalPracticeClaims } = renewalData.renewalSection

      return {
        isVisible: !!hasMalPracticeClaims,
        value: hasMalPracticeClaims
      }
    }
    return { isVisible: false, value: false }
  })
  const [hasMalpracticeClaimReported, setHasMalpracticeClaimReported] = useState<MalpracticeData>(() => {
    if(renewalData?.renewalSection?.hasMalPracticeClaimsReported !== undefined) {
      const { hasMalPracticeClaimsReported } = renewalData.renewalSection

      return {
        isVisible: hasMalPracticeClaimsReported !== undefined,
        value: hasMalPracticeClaimsReported
      }
    }
    return { isVisible: false, value: false }
  })
  const [hasMalpracticeBasis, setHasMalpracticeBasis] = useState<MalpracticeData>(() => {
    if(renewalData?.renewalSection?.hasMalPracticeBasis) {
      const { hasMalPracticeBasis } = renewalData.renewalSection

      return {
        isVisible: !!hasMalPracticeBasis,
        value: hasMalPracticeBasis
      }
    }
    return { isVisible: false, value: false }
  })
  const [hasMalpracticeBasisReported, setHasMalpracticeBasisReported] = useState<MalpracticeData>(() => {
    if(renewalData?.renewalSection?.hasMalPracticeBasisReported !== undefined) {
      const { hasMalPracticeBasisReported } = renewalData.renewalSection

      return {
        isVisible: hasMalPracticeBasisReported !== undefined,
        value: hasMalPracticeBasisReported
      }
    }
    return { isVisible: false, value: false }
  })

  const activeAttorneys = allActiveAttorneys(renewalData)

  const isAllSet = () => {
    return !isClaimsTriggered(({
      ...renewalData,
      renewalSection: {
        ...renewalData?.renewalSection,
        hasMalPracticeClaims: hasMalpracticeClaim.value,
        hasMalPracticeClaimsReported: hasMalpracticeClaimReported.value,
        hasMalPracticeBasis: hasMalpracticeBasis.value,
        hasMalPracticeBasisReported: hasMalpracticeBasisReported.value
      }
    }))
  }

  const updateClaims = useCallback((index: number, data: Partial<ClaimData>) => {
    setClaims(prev => prev.map((pv, idx) => idx === index ? { ...pv, ...data } : pv))
  }, [])

  const handleAddAnotherClaim = () => {
    setClaims(prev => [...prev, {}])
  }

  const updatedApplicationData = (): RenewalData => ({ 
    ...renewalData,
    currentPage: window.location.pathname,
    renewalSection: {
      ...renewalData?.renewalSection,
      hasMalPracticeClaims: hasMalpracticeClaim.value,
      hasMalPracticeClaimsReported: hasMalpracticeClaimReported.value,
      hasMalPracticeBasis: hasMalpracticeBasis.value,
      hasMalPracticeBasisReported: hasMalpracticeBasisReported.value,
      claims
    }
  })

  const goBackRoute = () => {
    return '/customer/renew/questions/main'
  }

  const validatePage = () => {
    const validation = claims.map(claim => {
      try {
        if(!!claim?.claimDate) {
          string().trim().length(10).validDate('Invalid Date').validateSync(claim?.claimDate)
        }
        if(!!claim?.allegedErrorDate) {
          string().trim().length(10).validDate('Invalid Date').validateSync(claim?.allegedErrorDate)
        }
        return true
      } catch(e) {
        return false
      }
    })

    return !validation.some(valid => valid === false)
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Please fill out the following regarding your claim.</DefaultTitle>
        <GreenContainer>
          <div className='green-container-content'>
            <h6>You’re being asked to fill out this supplemental claim information based on your answers to the following questions.</h6>
            <span>If this information is incorrect, please change your answers.</span>
            <div>
              <LeftBar />
              <GreenContainerQuestionWrapper>
                {hasMalpracticeClaim.isVisible ? (
                  <div className='green-container-question-div'>
                    <StepBullet>9</StepBullet>
                    <FlexDiv dir='column' gap="24px">
                      <p>
                        Has the firm or any member been the subject of a legal malpractice claim in the past 10 years?
                      </p>
                      <FlexDiv gap="16px">
                        <OptionButton
                          checked={hasMalpracticeClaim.value === true}
                          onChange={() => {
                            setHasMalpracticeClaim(prev => ({ ...prev, value: true }))
                            setHasMalpracticeClaimReported({ isVisible: true, value: renewalData?.renewalSection?.hasMalPracticeClaimsReported })
                          }}
                        >
                          Yes
                        </OptionButton>
                        <OptionButton
                          checked={hasMalpracticeClaim.value === false}
                          onChange={() => {
                            setHasMalpracticeClaim(prev => ({ ...prev, value: false }))
                            setHasMalpracticeClaimReported({ isVisible: false, value: undefined })
                          }}
                        >
                          No
                        </OptionButton>
                      </FlexDiv>
                      {hasMalpracticeClaimReported.isVisible ? (
                        <FlexDiv gap="24px">
                          <LeftBar />
                          <FlexDiv dir="column">
                            <span style={{margin: 0}}>Has the matter been previously reported to TLIE?</span>
                            <FlexDiv gap="16px" margin="8px 0 0 0">
                              <OptionButton
                                checked={hasMalpracticeClaimReported.value === true}
                                onChange={() => setHasMalpracticeClaimReported(prev => ({ ...prev, value: true }))}
                              >
                                Yes
                              </OptionButton>
                              <OptionButton
                                checked={hasMalpracticeClaimReported.value === false}
                                onChange={() => setHasMalpracticeClaimReported(prev => ({ ...prev, value: false }))}
                              >
                                No
                              </OptionButton>
                            </FlexDiv>
                          </FlexDiv>
                        </FlexDiv>
                      ) : null}
                    </FlexDiv>
                  </div>
                ) : null}
                {hasMalpracticeBasis.isVisible ? (
                  <div className='green-container-question-div'>
                    <StepBullet>10</StepBullet>
                    <FlexDiv dir='column' gap="24px">
                      <p>
                      Does any firm member or employee (including non-attorneys) of the firm know, 
                      have reason to know, or have any basis to believe that a circumstance, act, 
                      error or omission might reasonably be expected to be the basis for a professional 
                      liability claim against the firm or any attorney?
                      </p>
                      <FlexDiv gap="16px">
                        <OptionButton
                          checked={hasMalpracticeBasis.value === true}
                          onChange={() => {
                            setHasMalpracticeBasis(prev => ({ ...prev, value: true }))
                            setHasMalpracticeBasisReported(({ isVisible: true, value: renewalData?.renewalSection?.hasMalPracticeBasisReported }))
                          }}
                        >
                          Yes
                        </OptionButton>
                        <OptionButton
                          checked={hasMalpracticeBasis.value === false}
                          onChange={() => {
                            setHasMalpracticeBasis(prev => ({ ...prev, value: false }))
                            setHasMalpracticeBasisReported(({ isVisible: false, value: undefined }))
                          }}
                        >
                          No
                        </OptionButton>
                      </FlexDiv>
                      {hasMalpracticeBasisReported.isVisible ? (
                        <FlexDiv gap="24px">
                          <LeftBar />
                          <FlexDiv dir="column">
                            <span style={{margin: 0}}>Has the matter been previously reported to TLIE?</span>
                            <FlexDiv gap="16px" margin="8px 0 0 0">
                              <OptionButton
                                checked={hasMalpracticeBasisReported.value === true}
                                onChange={() => setHasMalpracticeBasisReported(prev => ({ ...prev, value: true }))}
                              >
                                Yes
                              </OptionButton>
                              <OptionButton
                                checked={hasMalpracticeBasisReported.value === false}
                                onChange={() => setHasMalpracticeBasisReported(prev => ({ ...prev, value: false }))}
                              >
                                No
                              </OptionButton>
                            </FlexDiv>
                          </FlexDiv>
                        </FlexDiv>
                      ) : null}
                    </FlexDiv>
                  </div>
                ) : null}
              </GreenContainerQuestionWrapper>
            </div>
          </div>
        </GreenContainer>
        {isAllSet() ? (
          <GreenContainer>
            <div className='claim-all-set'>
              <div className='claim-all-set-icon'>
                <img src={flagWithCheckImg} alt="All Set" />
              </div>
              <div className='claim-all-set-message'>
                <h3>All Set!</h3>
                <h4>You no longer need to complete this section.</h4>
              </div>
            </div>
          </GreenContainer>
        ) : (
          <ClaimsContainer>
            <span className='claims-form-header-subtitle'>PLEASE FILL OUT THE FOLLOWING TO THE BEST OF YOUR ABILITY</span>
            <span className='claims-form-header-subtitle'>If you do not know, leave blank.</span>
            {claims.map((claim, index) => (
              <ClaimItemForm
                key={index}
                claim={claim}
                claimIndex={index}
                attorneys={activeAttorneys}
                setClaim={(data) => updateClaims(index, data)}
                isIncorrect={isIncorrect && !validatePage()}
                errorCodes={firmRenewalClaimErrorCodes.map(e => `${e}:${index}`)}
              />
            ))}
            <div className='claims-add-button'>
              <Button variant='secondary' onClick={handleAddAnotherClaim}>
                Add another claim
              </Button>
            </div>
          </ClaimsContainer>
        )}
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute={goBackRoute()}
        nextRoute='/customer/renew/quote/select'
        updatedApplication={updatedApplicationData()}
        isFormValid={validatePage()}
        beforeSaveContinueClick={() => !validatePage() ? setIsIncorrect(true) : null}
      />
    </ApplicationBodyContainer>
  )
}

export default Claims